import * as React from "react";
import { useCallback, useState } from "react";
import { DividerProps, styled, Theme } from "@mui/material";
import { KahunSecondaryTypography } from "../../../../style/akTypography";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../../util/aKaunGeneralUtil";
import { ExpandButton } from "../../common/expandButton";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useScreenSize } from "../../util/useScreenSize";

//region [[ Styles ]]
interface HomePageVisualStepBoxViewProps extends DividerProps {
  expanded?: boolean;
}

const HomePageVisualStepBoxView = styled("div")(
  ({
    theme,
    expanded,
  }: { theme?: Theme } & HomePageVisualStepBoxViewProps) => ({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: "14px",
      background: "white",
      padding: "31px 25px",
      height: "103px",
      width: "274px",
    },
    [theme.breakpoints.down(MobileView)]: {
      background: "transparent",
      // Let flexBox organize the padding responsively
      padding: "0",
      margin: "0",
      maxWidth: "fit-content",
      // Let the words not span too much
      minWidth: "65px",
    },
  }),
);

const ArrowContainer = styled(ExpandButton)(({ theme }) => ({
  position: "absolute",
  right: "13px",
  bottom: "31px",
  "& svg": {
    fill: KahunColorMap.Accent,
  },
  [theme.breakpoints.down(MobileView)]: {
    display: "none",
  },
}));

const TitleText = styled(KahunSecondaryTypography)(({ theme }) => ({
  color: KahunColorMap.Accent,
  textTransform: "uppercase",
  fontWeight: 500,
  fontSize: "15px",
  lineHeight: "20px",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "13px",
  },
}));

const ContentText = styled(KahunSecondaryTypography)(({ theme }) => ({
  color: KahunColorMap.Default,
  fontWeight: 500,
  fontSize: "17px",
  lineHeight: "22px",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "11px",
    lineHeight: "13px",
  },
}));

const StepContent = styled("div")(({ theme }) => ({
  backgroundColor: KahunColorMap.PrimaryDim,
  color: "white",
  borderBottomLeftRadius: "14px",
  borderBottomRightRadius: "14px",
  margin: "18px -25px",
  padding: "18px",
  transition: "all 0.5s ease-out",
  "&.expanded": {
    height: "200px",
    // padding: "18px",
    opacity: 1,
  },
  "&.collapsed": {
    height: 0,
    padding: 0,
    opacity: 0,
  },
  [theme.breakpoints.down(MobileView)]: {
    display: "none",
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface HopePageVisualStepBoxProps {
  className?: string;
  visualStepItem: {
    title: string;
    content: string;
    shortText?: string;
  };
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const HomePageVisualStepBox = ({
  visualStepItem,
  ...props
}: HopePageVisualStepBoxProps) => {
  const { isMobile } = useScreenSize();
  const [isOpen, setIsOpen] = useState<boolean>();

  const handleClick = useCallback(() => {
    setIsOpen(!isMobile && !isOpen);
  }, [isMobile, isOpen, setIsOpen]);

  return (
    <HomePageVisualStepBoxView
      className={props.className}
      onClick={handleClick}
    >
      <TitleText>{visualStepItem.title}</TitleText>
      <ContentText>{visualStepItem.shortText}</ContentText>
      <ArrowContainer expanded={isOpen} />
      <StepContent className={isOpen ? "expanded" : "collapsed"}>
        {visualStepItem.content}
      </StepContent>
    </HomePageVisualStepBoxView>
  );
};
